import { alpha, createTheme } from '@mui/material/styles';
import DraggableDialogPaper from './components/Common/DraggableDialogPaper';
import { preventBackdropDismiss } from './Utils/dialogDismissal';

const theme = createTheme({
  palette: {
    primary: {
      main: '#5757b0',
      light: '#7777d0',
      dark: '#4545a0',
      contrastText: '#fff'
    },
    secondary: {
      main: '#f59e0b'
    },
    background: {
      default: '#f7f7ff',
      paper: '#ffffff'
    },
    text: {
      primary: '#222',
      secondary: '#666'
    }
  },
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    h4: {
      fontWeight: 600
    }
  },
  components: {
    // Dialogs, popovers and temporary drawers all share MUI's Modal root.
    MuiModal: {
      defaultProps: { onClickCapture: preventBackdropDismiss }
    },
    // Also cover standalone/custom portals built directly on Backdrop.
    MuiBackdrop: {
      defaultProps: { onClickCapture: preventBackdropDismiss }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small'
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiDrawer: {
      defaultProps: {
        anchor: 'right'
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(3),
          // MUI forces `padding-top: 0` when DialogContent follows DialogTitle.
          // Use a CSS variable so dialogs can keep a shared default while
          // full-bleed viewers and compact dialogs can explicitly opt out.
          '.MuiDialogTitle-root + &': {
            paddingTop: 'var(--dialog-content-top-padding, 20px)'
          },
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2)
          }
        }),
        dividers: ({ theme }) => ({ borderColor: alpha(theme.palette.primary.main, 0.12) })
      }
    },
    MuiDialog: {
      defaultProps: {
        PaperComponent: DraggableDialogPaper
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& > .MuiBackdrop-root': {
            backgroundColor: alpha('#22223e', 0.46),
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)'
          },
          '& .MuiDialogActions-root > .MuiButton-root': {
            minHeight: 40,
            paddingInline: theme.spacing(2),
            borderRadius: 10,
            fontWeight: 600
          },
          '& .MuiButtonBase-root:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 3
          }
        }),
        paper: ({ theme }) => ({
          borderRadius: 20,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          backgroundImage: 'none',
          boxShadow: '0 24px 80px rgba(28, 28, 65, 0.22), 0 4px 16px rgba(28, 28, 65, 0.08)',
          [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(2),
            maxHeight: 'calc(100% - 32px)'
          }
        }),
        paperFullWidth: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: { width: 'calc(100% - 32px)' }
        }),
        paperFullScreen: {
          borderRadius: 0,
          border: 0,
          margin: 0,
          maxHeight: 'none',
          '@media (max-width: 599.95px)': { margin: 0, width: '100%', maxHeight: 'none' }
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          cursor: 'move',
          userSelect: 'none',
          padding: theme.spacing(2.5, 3),
          backgroundColor: alpha(theme.palette.primary.main, 0.055),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          fontSize: '1.2rem',
          fontWeight: 700,
          lineHeight: 1.4,
          overflowWrap: 'anywhere',
          [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) }
        })
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2, 3),
          gap: theme.spacing(1),
          flexWrap: 'wrap',
          backgroundColor: alpha(theme.palette.primary.main, 0.025),
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          [theme.breakpoints.down('sm')]: { padding: theme.spacing(1.5, 2) }
        }),
        spacing: { '& > :not(:first-of-type)': { marginLeft: 0 } }
      }
    }
  }
});

export default theme;
